<template>
  <div class="business-details">
    <h2>{{ t('businessDetails.Title') }}</h2>

    <div v-if="loading" class="loading">{{ t('labels.Loading') }}...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else>
      <div class="toolbar">
        <KButton @click="toggleView" :theme-color="'secondary'">
          {{ showChart ? t('businessDetails.ShowGrid') : t('businessDetails.ShowChart') }}
        </KButton>
        <KButton @click="exportToExcel" :theme-color="'primary'">
          {{ t('orderList.ExportButton') }}
        </KButton>
      </div>

      <div v-if="showChart" class="chart-container">
        <Line :data="chartData" :options="chartOptions" />
      </div>

      <div v-else class="grid-wrapper">
        <div class="grid-container">
          <Grid
            :data-items="gridData"
            :columns="columns"
            :resizable="true"
          >
          </Grid>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { Grid } from '@progress/kendo-vue-grid'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { saveExcel } from '@progress/kendo-vue-excel-export'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { PWSHstBatchGetList, PwsBusinessDetailsGet } from '@/api/Business'
import { PwsDynamicReportGet } from '@/api/ConsultantCorner'
import { FormatCurrency, FormatNumber } from '@/services/currencyFormatter'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'
// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const { t } = useI18n()
const logger = useLogger()

interface Props {
  beeNumber?: number
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
}>()

const loading = ref(true)
const error = ref('')
const batchHistory = ref<any[]>([])
const reportDefinition = ref<any>(null)
const businessDetailsData = ref<any[]>([])
const showChart = ref(false)

// Fetch report definition to get metric columns
async function loadReportDefinition() {
  try {
    const response = await PwsDynamicReportGet('BusinessDetails', props.beeNumber)
    logger.debug('Report definition response:', response)

    if (response && response.item) {
      reportDefinition.value = response.item

      // Extract column names (excluding filter-only columns)
      const columnNames = response.item.columns
        .filter((col: any) => !col.isFilterOnly)
        .map((col: any) => col.columnName)

      logger.debug('Column names:', columnNames)
      return columnNames
    }
    return []
  } catch (err: any) {
    error.value = err.message || 'Failed to load report definition'
    logger.error('Error loading report definition:', err)
    return []
  }
}

// Fetch batch history data
async function loadBatchHistory() {
  try {
    // Request the 10 most recent batches sorted by history date descending
    const response = await PWSHstBatchGetList(10, 0)
    logger.debug('Batch history response:', response)

    if (response && response.items) {
      // Keep newest first (left to right)
      batchHistory.value = response.items
      return response.items
    }
    return []
  } catch (err: any) {
    error.value = err.message || 'Failed to load batch history'
    logger.error('Error loading batch history:', err)
    return []
  }
}

// Fetch business details for each batch
async function loadBusinessDetails(columnNames: string[], batches: any[]) {
    const detailsPromises = batches.map(async (batch) => {
      try {
        const result = await PwsBusinessDetailsGet(columnNames, batch.batchNumber || batch.proBatchGuid, props.beeNumber)

        // Transform the response from array of {fieldName, value, format} to object with fieldName as keys
        const transformedResult: any = {}
        if (result && result.currentFields) {
          result.currentFields.forEach((field: any) => {
            let value = field.value === 'Not Implemented' ? 0 : field.value

            // Format based on field format type
            if (field.format === 'Sales Org Currency' && value !== 0) {
              value = FormatCurrency(value, null) || value
            } else if (field.format === 'Decimal' && typeof value === 'number') {
              // Check if value has decimals
              const decimals = Number.isInteger(value) ? 0 : 2
              value = FormatNumber(value, decimals) || value
            }

            transformedResult[field.fieldName] = value
          })
        }

        return transformedResult
      } catch (err: any) {
        logger.warn(`Failed to load business details for batch ${batch.batchNumber || batch.proBatchGuid}:`, err)
        // Return empty object with zeros for all columns on failure
        const emptyResult: any = {}
        columnNames.forEach(col => {
          emptyResult[col] = 0
        })
        return emptyResult
      }
    })

    const results = await Promise.all(detailsPromises)
    logger.debug('Business details results:', results)

    businessDetailsData.value = results
}

// Load all data
async function loadData() {
  loading.value = true
  error.value = ''

  try {
    // Step 1: Get report definition to know which columns to fetch
    const columnNames = await loadReportDefinition()

    if (columnNames.length === 0) {
      throw new Error(t('businessDetails.errors.noColumnsFoundInReportDefinition'))
    }

    // Step 2: Get batch history
    const batches = await loadBatchHistory()

    if (batches.length === 0) {
      throw new Error(t('businessDetails.errors.noBatchHistoryFound'))
    }

    // Step 3: Get business details for each batch
    await loadBusinessDetails(columnNames, batches)

  } catch (err: any) {
    error.value = err.message || t('businessDetails.errors.failedToLoadData')
    logger.error('Error loading data:', err)
  } finally {
    loading.value = false
  }
}

// Build grid columns dynamically
const columns = computed(() => {
  const cols: any[] = [
    {
      field: 'metric',
      title: t('businessDetails.Metrics'),
      width: resolveColumnWidth(250, t('businessDetails.Metrics')),
      locked: true
    }
  ]

  // Add a column for each batch period
  batchHistory.value.forEach((batch, index) => {
    // Use batchName from the batch response, fallback to formatted date if not available
    const columnTitle = batch.batchName || batch.batchNumber ||
                        new Date(batch.historyDate).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })

    cols.push({
      field: `period${index}`,
      title: columnTitle,
      width: resolveColumnWidth(150, columnTitle),
      locked: false
    })
  })

  return cols
})

// Build grid data with metrics as rows
const gridData = computed(() => {
  if (!reportDefinition.value || businessDetailsData.value.length === 0) {
    return []
  }

  // Get metric columns (non-filter columns)
  const metricColumns = reportDefinition.value.columns.filter((col: any) => !col.isFilterOnly)

  return metricColumns.map((column: any) => {
    const row: any = {
      metric: column.localizedColumnName || column.columnName
    }

    // Add value for each period from business details data
    businessDetailsData.value.forEach((details, index) => {
      // Value is already formatted with € symbol if needed (done in loadBusinessDetails)
      row[`period${index}`] = details[column.columnName] || 0
    })

    return row
  })
})

// Chart.js data structure
const chartData = computed(() => {
  if (!reportDefinition.value || businessDetailsData.value.length === 0) {
    return {
      labels: [],
      datasets: []
    }
  }

  // X-axis labels (batch names)
  const labels = batchHistory.value.map(batch =>
    batch.batchName || batch.batchNumber || 'Unknown'
  )

  // Get metric columns
  const metricColumns = reportDefinition.value.columns.filter((col: any) => !col.isFilterOnly)

  // Create datasets (one per metric)
  const datasets = metricColumns.map((column: any, index: number) => {
    // Generate a color for each line
    const hue = (index * 360) / metricColumns.length
    const color = `hsl(${hue}, 70%, 50%)`

    return {
      label: column.localizedColumnName || column.columnName,
      data: businessDetailsData.value.map(details => details[column.columnName] || 0),
      borderColor: color,
      backgroundColor: color,
      tension: 0.1
    }
  })

  return {
    labels,
    datasets
  }
})

// Chart.js options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const
    },
    title: {
      display: true,
      text: t('businessDetails.ChartTitle')
    }
  },
  scales: {
    y: {
      beginAtZero: true
    }
  }
}))

// Toggle between chart and grid view
function toggleView() {
  showChart.value = !showChart.value
}

// Export grid data to Excel
function exportToExcel() {
  saveExcel({
    data: gridData.value,
    fileName: 'BusinessDetails',
    columns: columns.value
  })
}

onMounted(() => {
  loadData()
})
</script>

<style lang="scss" scoped>
  .business-details {
    padding: $card-padding;
  }

.toolbar {
  display: flex;
  justify-content: flex-end;
  gap: $spacing-2-5;
  margin-bottom: $spacing-4;
}

.loading, .error {
  padding: $card-padding;
  text-align: center;
}

.error {
  color: red;
}

.grid-wrapper {
  margin-top: 20px;
  width: 100%;
  overflow-x: auto;
}

.grid-container {
  min-width: 992px; /* Bootstrap lg breakpoint */
}

.chart-container {
  margin-top: $spacing-5;
  padding: $card-padding;
  background: white;
  border-radius: $radius-sm;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  height: 500px;
}

  @media (max-width: 768px) {
    body {
      .business-details {
        h2 {
          @include typography-h2;
        }

        /* unstick the first column header on mobile */
        :deep(.k-table-th.k-grid-header-sticky) {
          position: relative;
        }

        :deep(.k-grid .k-grid-content-sticky) {
          position: relative;
        }
      }
    }
  }

@media (max-width: 991.98px) {
  .grid-container :deep(.k-column-title) {
    text-overflow: clip;
    white-space: normal;
    overflow: visible;
  }
}
</style>
